import { useMapGetter, useStore } from 'dashboard/composables/store';
import {
  filterAgentsByTeamMembers,
  getAgentsByUpdatedPresence,
  getSortedAgentsByAvailability,
} from 'dashboard/helper/agentHelper';
import { computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';

// Team members are fetched on demand; remembering the teams already requested
// avoids refetching when switching between conversations of the same team.
const requestedTeamIds = new Set();

/**
 * A composable function that provides a list of agents for assignment.
 *
 * @param {boolean} [includeNoneAgent=true] - Whether to include a 'None' agent option.
 * @param {Object} [options] - Options for the assignable agents list.
 * @param {boolean} [options.includeAgentBots=false] - Whether to include AgentBot assignees. Only pass this from surfaces that thread `assignee_type` through the assignment request.
 * @param {boolean} [options.filterByTeam=true] - When the conversation has a team, only list agents that belong to it.
 * @returns {Object} An object containing the agents list and assignable agents.
 */
export function useAgentsList(
  includeNoneAgent = true,
  { includeAgentBots = false, filterByTeam = true } = {}
) {
  const { t } = useI18n();
  const store = useStore();
  const currentUser = useMapGetter('getCurrentUser');
  const currentChat = useMapGetter('getSelectedChat');
  const currentAccountId = useMapGetter('getCurrentAccountId');
  const assignable = useMapGetter('inboxAssignableAgents/getAssignableAgents');
  const teamMembers = useMapGetter('teamMembers/getTeamMembers');

  const inboxId = computed(() => currentChat.value?.inbox_id);
  const teamId = computed(() => currentChat.value?.meta?.team?.id);
  const isAgentSelected = computed(() => currentChat.value?.meta?.assignee);

  watch(
    teamId,
    id => {
      if (!id || !filterByTeam || requestedTeamIds.has(id)) return;
      requestedTeamIds.add(id);
      store
        .dispatch('teamMembers/get', { teamId: id })
        .catch(() => requestedTeamIds.delete(id));
    },
    { immediate: true }
  );

  /**
   * Creates a 'None' agent object
   * @returns {Object} None agent object
   */
  const createNoneAgent = () => ({
    confirmed: true,
    name: t('AGENT_MGMT.MULTI_SELECTOR.LIST.NONE') || 'None',
    id: 0,
    role: 'agent',
    account_id: 0,
    email: 'None',
  });

  /**
   * @type {import('vue').ComputedRef<Array>}
   */
  const assignableAgents = computed(() => {
    const agents = inboxId.value
      ? assignable.value(inboxId.value, { includeAgentBots })
      : [];
    if (!filterByTeam || !teamId.value) return agents;

    return filterAgentsByTeamMembers(agents, teamMembers.value(teamId.value));
  });

  /**
   * @type {import('vue').ComputedRef<Array>}
   */
  const agentsList = computed(() => {
    const agents = (assignableAgents.value || []).map(agent =>
      !agent.name && agent.assignee_type === 'AgentBot'
        ? { ...agent, name: '-' }
        : agent
    );
    const agentsByUpdatedPresence = getAgentsByUpdatedPresence(
      agents,
      currentUser.value,
      currentAccountId.value
    );

    const filteredAgentsByAvailability = getSortedAgentsByAvailability(
      agentsByUpdatedPresence
    );

    return [
      ...(includeNoneAgent && isAgentSelected.value ? [createNoneAgent()] : []),
      ...filteredAgentsByAvailability,
    ];
  });

  return {
    agentsList,
    assignableAgents,
  };
}
